import { Stack } from '@mui/material';
import {
  ProductGroupMultiselectFilter,
  SideFilters,
  SuspenseWithSentryErrorBoundary,
} from '@retail/components';
import { MgSupplierItemSearchField, SelectDiscountGroups } from '@retail/products/components';
import { SelectSupplier } from '@retail/suppliers/components';
import { SupplierBase } from '@retail/suppliers/types';
import { FixedPriceTableContainer } from './FixedPriceTableContainer';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { MutableRefObject } from 'react';
import { useFilters } from '@retail/products/hooks';
import { DiscountGroup } from '@retail/products/types';
import { HierarchyFilterContext } from '@retail/my-assortment/types';
import { levelFilters, LevelFilters } from '@retail/products/utils';

interface Props {
  assortmentId: number;
  filterContext?: HierarchyFilterContext;
  isLoadingFilters?: boolean;
  isErrorFilters?: boolean;
  showFilters: boolean;
  apiRef: MutableRefObject<GridApiPremium | null>;
}

export function FixedPriceContainer({
  assortmentId,
  filterContext,
  isLoadingFilters,
  isErrorFilters,
  showFilters,
  apiRef,
}: Props) {
  const { filters, setFilters, updateFilter } = useFilters<LevelFilters>(levelFilters);

  return (
    <Stack flexGrow={1} flexDirection="row" overflow="hidden">
      <SuspenseWithSentryErrorBoundary>
        <FixedPriceTableContainer
          assortmentId={assortmentId}
          apiRef={apiRef}
          selectedSupplier={filters.supplierNumber ? Number(filters.supplierNumber) : undefined}
          selectedDiscountGroups={filters.discountGroupCodes}
          selectedProductGroups={filters.productGroupNumbers}
          query={filters.searchQuery ?? ''}
        />
      </SuspenseWithSentryErrorBoundary>

      <SideFilters showFilters={showFilters} sx={{ overflow: 'auto' }}>
        <SelectSupplier
          suppliers={filterContext?.suppliers ?? []}
          isLoading={isLoadingFilters}
          isError={isErrorFilters}
          selectedSupplier={filters.supplierNumber ? Number(filters.supplierNumber) : null}
          getSupplierId={(supplier: SupplierBase) => supplier.supplierNumber}
          getSupplierName={(supplier: SupplierBase) => supplier.name}
          selectSupplier={(supplierNumber) => {
            setFilters({
              supplierNumber: supplierNumber ? supplierNumber.toString() : undefined,
              discountGroupCodes: undefined,
            });
          }}
        />

        {!!filters.supplierNumber && (
          <SelectDiscountGroups
            discountGroups={filterContext?.discountGroupsForSupplier ?? []}
            selectedDiscountGroups={filters.discountGroupCodes.map(Number)}
            getDiscountGroupKey={({ id }: DiscountGroup) => id}
            selectDiscountGroups={(ids: number[]) =>
              setFilters({ discountGroupCodes: ids.map(String) })
            }
          />
        )}

        <MgSupplierItemSearchField
          searchTerm={filters.searchQuery ?? ''}
          isDisabled={false}
          size="small"
          onSubmitSearch={(searchQuery: string) => setFilters({ searchQuery: searchQuery })}
        />

        <ProductGroupMultiselectFilter
          productGroups={filterContext?.productGroups ?? []}
          selectedProductGroups={filters.productGroupNumbers}
          selectProductGroup={(groupNumber: string) =>
            updateFilter('productGroupNumbers', groupNumber)
          }
        />
      </SideFilters>
    </Stack>
  );
}
